import { IConfig, Permission } from '@alberta/konexi-shared';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'src/app/common/menu/menu-item';

import { Capacitor } from '@capacitor/core';
import {
  ArticleModelName,
  GroupModelName,
  InstitutionModelName,
  InsuranceContractModelName,
  PatientModelName,
  ProductGroupModelName,
  RegionModelName,
  StandardCareProposalModelName,
  UsersModelName,
} from '../../models/model-names';
import { AuthService } from '../../services/auth.service';

export class MainMenu {
  public static async get(
    isWebApp: boolean,
    authService: AuthService,
    translateService: TranslateService,
    knowledgeMenuItemName: string,
    tenantConfig: IConfig
  ): Promise<MenuItem[]> {
    const menuItems = [];
    menuItems.push(new MenuItem('Startseite', 'alberta-dashboard', '/dashboard'));
    if (await authService.userHasPermission(PatientModelName, Permission.Read)) {
      menuItems.push(new MenuItem('Patienten', 'alberta-person', '/patient'));
    }

    const showOrderList =
      tenantConfig?.orderListConfig?.enabled && (await authService.userHasPermission('orderList', Permission.Read));
    const showDocumentList =
      tenantConfig?.documentListConfig?.enabled &&
      (await authService.userHasPermission('documentList', Permission.Read));
    const showServiceTaskList =
      tenantConfig?.specializedServiceProviderConfig?.enabled &&
      tenantConfig?.serviceTaskListConfig?.enabled &&
      (await authService.userHasPermission('serviceTaskList', Permission.Read));
    const showServiceOrderList =
      tenantConfig?.specializedServiceProviderConfig?.enabled &&
      !(tenantConfig?.specializedServiceProviderConfig as any)?.showNewServices &&
      tenantConfig?.serviceOrderListConfig?.enabled &&
      (await authService.userHasPermission('serviceOrderList', Permission.Read));
    if (showOrderList || showDocumentList || showServiceTaskList || showServiceOrderList) {
      const listMenu = new MenuItem('Listen', 'alberta-lists');
      if (showOrderList) {
        listMenu.addChild(new MenuItem('Bestellungen', 'alberta-shoppingcart-full', '/list/orders'));
      }
      if (showDocumentList) {
        listMenu.addChild(new MenuItem('Dokumente', 'alberta-swodoc', '/list/documents'));
      }
      if (showServiceTaskList) {
        listMenu.addChild(
          new MenuItem(translateService.instant('entities.serviceTask'), 'alberta-calendar-check', '/list/service-task')
        );
      }
      if (showServiceOrderList) {
        listMenu.addChild(
          new MenuItem(
            translateService.instant('entities.serviceOrder'),
            'alberta-service-order',
            '/list/service-order'
          )
        );
      }
      menuItems.push(listMenu);
    }

    if (!isWebApp && (await authService.userHasPermission('onlineSearch', Permission.Read))) {
      menuItems.push(new MenuItem('Online-Suche', 'alberta-person', '/emergency'));
    }
    const notificationMenu = new MenuItem('Mitteilungen', 'alberta-chat');
    notificationMenu.addChild(new MenuItem('Fax-Protokoll', 'alberta-fax', '/fax'));
    menuItems.push(notificationMenu);

    await this.setManagementPages(translateService, authService, menuItems, isWebApp);
    await this.setInstitutionPages(authService, menuItems);

    const hasPortalPermissions =
      (tenantConfig?.specializedServiceProviderConfig?.enabled &&
        (await authService.userHasPermission('invoicing', Permission.Read))) ||
      (await authService.userHasPermission('swodoc', Permission.Write));
    if (Capacitor.isNativePlatform() === false && hasPortalPermissions) {
      menuItems.push(new MenuItem('Portal', 'alberta-portal', '/portal'));
    }

    const settingsMenu = new MenuItem('Einstellungen', 'alberta-setting');
    settingsMenu.addChild(new MenuItem('Allgemein', 'alberta-hammer', '/setting'));
    if (await authService.userHasPermission('calendar', Permission.Read)) {
      settingsMenu.addChild(new MenuItem('Kalender', 'alberta-activities', '/setting/calendar'));
    }

    if (isWebApp) {
      if (await this.hasAnyUsermanagementPermission(authService)) {
        settingsMenu.addChild(new MenuItem('Rechtemanagement', 'alberta-key', '/rights-management'));
      }
      if (await this.userIsItLabs(authService)) {
        settingsMenu.addChild(new MenuItem('Wartungsmanagement', 'alberta-clock', '/maintenance'));
      }
      if (await authService.isAgent()) {
        settingsMenu.addChild(new MenuItem('Patienten-App', 'alberta-support', '/agent-admin'));
      }
    }
    menuItems.push(settingsMenu);

    if (knowledgeMenuItemName) {
      menuItems.push(new MenuItem(knowledgeMenuItemName, 'alberta-website-filled', '/knowledge-base'));
    }
    return menuItems;
  }
  private static async setInstitutionPages(authService: AuthService, menuItems: MenuItem[]): Promise<void> {
    if (await authService.userHasPermission(InstitutionModelName, Permission.Read)) {
      const instituionsMenu = new MenuItem('CRM', 'alberta-crm');

      instituionsMenu.addChild(new MenuItem('Apotheken', 'alberta-pharmacy', '/pharmacy'));
      instituionsMenu.addChild(new MenuItem('Ärzte', 'alberta-doctor', '/doctor'));
      instituionsMenu.addChild(new MenuItem('Kliniken', 'alberta-hospital', '/hospital'));
      instituionsMenu.addChild(new MenuItem('Pflegedienste', 'alberta-nursingservice', '/nursing-service'));
      instituionsMenu.addChild(new MenuItem('Pflegeheime', 'alberta-nursinghome', '/nursing-home'));

      menuItems.push(instituionsMenu);
    }
  }
  private static async setManagementPages(
    translateService: TranslateService,
    authService: AuthService,
    menuItems: MenuItem[],
    isWebApp
  ): Promise<void> {
    if (isWebApp) {
      if (await authService.userHasPermission(InsuranceContractModelName, Permission.Read)) {
        menuItems.push(new MenuItem('Verträge', 'alberta-contract', '/insurance-contract'));
      }
    }
    const productMenu = new MenuItem('Produkte', 'alberta-produce');
    if (await authService.userHasPermission(ArticleModelName, Permission.Read)) {
      productMenu.addChild(new MenuItem('Artikel', 'alberta-article', isWebApp ? '/article' : '/article-mobile'));
    }
    if (isWebApp) {
      if (await authService.userHasPermission(ProductGroupModelName, Permission.Read)) {
        productMenu.addChild(new MenuItem('Warengruppen', 'alberta-productgroup', '/product-group'));
      }
      if (await authService.userHasPermission(StandardCareProposalModelName, Permission.Read)) {
        productMenu.addChild(
          new MenuItem(
            translateService.instant('entities.standardCareProposal'),
            'alberta-stdcareproposal',
            '/standard-care-proposal'
          )
        );
      }
    }
    if (productMenu.children && productMenu.children.length > 0) {
      menuItems.push(productMenu);
    }
  }

  private static async hasAnyUsermanagementPermission(authService: AuthService): Promise<boolean> {
    return (
      (await authService.userHasPermission(UsersModelName, Permission.Read)) ||
      (await authService.userHasPermission(GroupModelName, Permission.Read)) ||
      (await authService.userHasPermission(RegionModelName, Permission.Read))
    );
  }

  private static async userIsItLabs(authService: AuthService): Promise<boolean> {
    const tenantName = await authService.getCurrentUserTenantName();
    return tenantName === '1TLabs';
  }
}
